import React, { useState, useEffect, useCallback } from "react";
import {
  BarChart2, TrendingUp, DollarSign, Globe, RefreshCw,
  ChevronDown, ChevronUp, Rss, Shield, Newspaper, ArrowUpRight,
} from "lucide-react";
import { SectionLabel } from "./atoms";
import { useAdminCtx } from "./AdminShell";

/* ============================================================
   DNH Admin — Content Economics
   Business intelligence: story coverage, LLM spend, and
   conversion opportunity by country.
   ============================================================ */

const BASE = process.env.REACT_APP_BACKEND_URL;

const PERIODS = [
  { key: "daily",     label: "Today" },
  { key: "weekly",    label: "7 Days" },
  { key: "monthly",   label: "30 Days" },
  { key: "quarterly", label: "90 Days" },
  { key: "yearly",    label: "1 Year" },
];

const TABS = [
  { key: "coverage",   label: "Coverage",          icon: <Globe size={13} /> },
  { key: "spend",      label: "LLM Spend",          icon: <DollarSign size={13} /> },
  { key: "conversion", label: "Conversion Signals", icon: <TrendingUp size={13} /> },
];

// ── Helpers ──────────────────────────────────────────────────
function fmt$(n) {
  if (!n) return "$0.00";
  if (n >= 1) return `$${n.toFixed(2)}`;
  return `$${n.toFixed(4)}`;
}
function fmtK(n) {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}
function pct(a, b) {
  if (!b) return "—";
  return `${((a / b) * 100).toFixed(1)}%`;
}

// ── Summary cards ─────────────────────────────────────────────
const SummaryCard = ({ label, value, sub, accent = "text-[var(--ink)]", icon }) => (
  <div className="bg-[var(--surface-2)] border border-[var(--hairline)] rounded-[6px] px-4 py-3">
    <div className={`flex items-center gap-1.5 mb-1.5 ${accent}`}>
      {icon}
      <span className="font-mono text-[10px] uppercase tracking-[0.12em] text-[var(--muted)]">{label}</span>
    </div>
    <div className="font-serif text-[28px] leading-none text-[var(--ink)]">{value}</div>
    {sub && <div className="font-mono text-[10.5px] text-[var(--muted)] mt-1">{sub}</div>}
  </div>
);

// ── Source type legend ────────────────────────────────────────
const TypeBadge = ({ type }) => {
  const cfg = {
    rss:         { label: "RSS",         cls: "bg-blue-50 text-blue-700 border-blue-200" },
    official:    { label: "Official",    cls: "bg-amber-50 text-amber-700 border-amber-200" },
    publication: { label: "Publication", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  }[type] || { label: type, cls: "bg-[var(--surface-2)] text-[var(--ink-2)] border-[var(--hairline)]" };
  return (
    <span className={`inline-flex items-center h-5 px-2 rounded-[4px] border font-mono text-[9.5px] uppercase tracking-[0.06em] ${cfg.cls}`}>
      {cfg.label}
    </span>
  );
};

// ── Bar spark ─────────────────────────────────────────────────
const MiniBar = ({ value, max, color = "bg-[var(--crimson)]" }) => {
  const w = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="w-full h-1.5 bg-[var(--hairline)] rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${w}%` }} />
    </div>
  );
};

// ── Coverage tab ──────────────────────────────────────────────
function CoverageTab({ rows, totals }) {
  const maxStories = Math.max(...rows.map(r => r.total_stories), 1);
  return (
    <div data-testid="ce-coverage-tab">
      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <TypeBadge type="rss" />
        <span className="font-mono text-[10.5px] text-[var(--muted)]">All {totals.countries} countries use RSS as ingestion mechanism</span>
        <span className="ml-auto font-mono text-[10px] text-[var(--muted)]">
          {fmtK(totals.stories)} stories · {fmtK(totals.rewritten)} rewritten · {fmtK(totals.with_brief)} with brief
        </span>
      </div>
      <div className="border border-[var(--hairline)] rounded-[6px] overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="bg-[var(--surface-2)] border-b border-[var(--hairline)]">
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Country</th>
              <th className="text-left px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Source Types</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">RSS Total</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Rewritten</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Brief Ready</th>
              <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)] w-[100px]">Coverage</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.country} className={`border-b border-[var(--hairline)] ${i % 2 === 0 ? "" : "bg-[var(--surface-2)]/40"}`}
                data-testid={`ce-row-${r.country}`}>
                <td className="px-4 py-2.5 font-serif text-[13px] text-[var(--ink)]">{r.country_label}</td>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-1 flex-wrap">
                    <span className="font-mono text-[10px] text-amber-600">{r.official_src} Off.</span>
                    <span className="text-[var(--hairline-strong)]">·</span>
                    <span className="font-mono text-[10px] text-emerald-600">{r.publication_src} Pub.</span>
                  </div>
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-[11px] text-[var(--ink)]">{fmtK(r.rss_total)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-[11px] text-[var(--ink-2)]">
                  {fmtK(r.rewritten)}
                  <span className="text-[var(--muted)] ml-1">({r.rewrite_pct}%)</span>
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-[11px] text-[var(--ink-2)]">
                  {fmtK(r.has_brief)}
                  <span className="text-[var(--muted)] ml-1">({r.brief_pct}%)</span>
                </td>
                <td className="px-3 py-2.5">
                  <MiniBar value={r.total_stories} max={maxStories} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── LLM Spend tab ─────────────────────────────────────────────
function SpendTab({ rows, totals }) {
  const maxCost = Math.max(...rows.map(r => r.est_total_cost), 0.0001);
  return (
    <div data-testid="ce-spend-tab">
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <SummaryCard label="Rewrite Cost" value={fmt$(totals.rewrite_cost)}
          sub="LLM rewrites on free RSS"
          accent="text-blue-600" icon={<Rss size={13} />} />
        <SummaryCard label="Brief Generation" value={fmt$(totals.brief_cost)}
          sub="Full briefs + intelligence"
          accent="text-[var(--crimson)]" icon={<BarChart2 size={13} />} />
        <SummaryCard label="Total LLM Spend" value={fmt$(totals.total_llm_cost)}
          sub="All functions combined"
          accent="text-[var(--ink)]" icon={<DollarSign size={13} />} />
      </div>
      <div className="border border-[var(--hairline)] rounded-[6px] overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="bg-[var(--surface-2)] border-b border-[var(--hairline)]">
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Country</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Stories</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Rewrite Cost</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Brief Cost</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Total Cost</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Per Story</th>
              <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)] w-[90px]">Spend</th>
            </tr>
          </thead>
          <tbody>
            {[...rows].sort((a, b) => b.est_total_cost - a.est_total_cost).map((r, i) => (
              <tr key={r.country} className={`border-b border-[var(--hairline)] ${i % 2 === 0 ? "" : "bg-[var(--surface-2)]/40"}`}>
                <td className="px-4 py-2.5 font-serif text-[13px] text-[var(--ink)]">{r.country_label}</td>
                <td className="px-3 py-2.5 text-right font-mono text-[11px] text-[var(--muted)]">{fmtK(r.total_stories)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-[11px] text-blue-600">{fmt$(r.est_rewrite_cost)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-[11px] text-[var(--crimson)]">{fmt$(r.est_brief_cost)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-[11px] font-bold text-[var(--ink)]">{fmt$(r.est_total_cost)}</td>
                <td className="px-3 py-2.5 text-right font-mono text-[10.5px] text-[var(--muted)]">{fmt$(r.cost_per_story)}</td>
                <td className="px-3 py-2.5">
                  <MiniBar value={r.est_total_cost} max={maxCost} color="bg-[var(--crimson)]" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="mt-3 font-mono text-[10px] text-[var(--muted)]">
        * Costs are prorated estimates based on story-count ratios. Exact per-country tracking logged from today onwards.
      </p>
    </div>
  );
}

// ── Conversion tab ────────────────────────────────────────────
function ConversionTab({ rows, totals }) {
  // Sort: many stories + low brief % = highest opportunity
  const opps = [...rows]
    .filter(r => r.total_stories >= 5)
    .sort((a, b) => {
      const scoreA = a.total_stories * (1 - a.brief_pct / 100);
      const scoreB = b.total_stories * (1 - b.brief_pct / 100);
      return scoreB - scoreA;
    })
    .slice(0, 20);
  const maxScore = opps[0] ? opps[0].total_stories * (1 - opps[0].brief_pct / 100) : 1;

  return (
    <div data-testid="ce-conversion-tab">
      <div className="mb-5 px-4 py-3 border border-[var(--hairline)] rounded-[6px] bg-[var(--surface-2)]">
        <p className="font-mono text-[11px] text-[var(--ink-2)] leading-relaxed">
          <span className="text-[var(--crimson)] font-bold">Strategy:</span> Countries with
          many stories but low brief coverage are your best upgrade opportunities. Users
          read free highlights → hit the paywall → subscribe. Focus brief generation on
          high-volume, low-coverage countries first.
        </p>
      </div>
      <div className="border border-[var(--hairline)] rounded-[6px] overflow-hidden">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="bg-[var(--surface-2)] border-b border-[var(--hairline)]">
              <th className="text-left px-4 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">#</th>
              <th className="text-left px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Country</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Stories</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Brief %</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Rewrite %</th>
              <th className="px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Opportunity</th>
              <th className="text-right px-3 py-2.5 font-mono text-[10px] uppercase tracking-[0.1em] text-[var(--muted)]">Sources</th>
            </tr>
          </thead>
          <tbody>
            {opps.map((r, i) => {
              const score = r.total_stories * (1 - r.brief_pct / 100);
              const tier = i < 5 ? "🔴 High" : i < 10 ? "🟡 Med" : "🟢 Low";
              return (
                <tr key={r.country} className={`border-b border-[var(--hairline)] ${i % 2 === 0 ? "" : "bg-[var(--surface-2)]/40"}`}>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-[var(--muted)]">{i + 1}</td>
                  <td className="px-3 py-2.5 font-serif text-[13px] text-[var(--ink)]">{r.country_label}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-[11px] text-[var(--ink)]">{fmtK(r.total_stories)}</td>
                  <td className="px-3 py-2.5 text-right">
                    <span className={`font-mono text-[11px] ${r.brief_pct < 20 ? "text-[var(--crimson)]" : r.brief_pct < 50 ? "text-amber-600" : "text-emerald-600"}`}>
                      {r.brief_pct}%
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-[11px] text-[var(--ink-2)]">{r.rewrite_pct}%</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-2">
                      <MiniBar value={score} max={maxScore} color={i < 5 ? "bg-[var(--crimson)]" : i < 10 ? "bg-amber-400" : "bg-emerald-400"} />
                      <span className="font-mono text-[9px] text-[var(--muted)] shrink-0">{tier}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono text-[10.5px] text-[var(--muted)]">
                    {r.official_src > 0 && <span className="text-amber-600">{r.official_src} Off </span>}
                    <span className="text-emerald-600">{r.publication_src} Pub</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════
// Main ContentEconomics component
// ══════════════════════════════════════════════════════════════
export default function ContentEconomics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState("weekly");
  const { subKey, setSubKey } = useAdminCtx() || {};
  const activeTab = subKey || "coverage";
  const setActiveTab = (key) => setSubKey?.(key);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/api/admin/content-economics?period=${period}`, { credentials: "include" });
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const totals = data?.totals || {};
  const rows   = data?.by_country || [];

  return (
    <section className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 py-8" data-testid="content-economics">

      {/* Header row */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <div>
          <SectionLabel>RSS · Official · Publications</SectionLabel>
          <h2 className="font-serif text-[22px] text-[var(--ink)] mt-0.5">Content Economics</h2>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Period selector */}
          <div className="flex items-center gap-1 bg-[var(--surface-2)] border border-[var(--hairline)] rounded-full p-1">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => setPeriod(p.key)}
                data-testid={`ce-period-${p.key}`}
                className={`h-6 px-3 rounded-full font-mono text-[10px] uppercase tracking-[0.08em] transition-colors ${
                  period === p.key
                    ? "bg-[var(--ink)] text-[var(--paper)]"
                    : "text-[var(--muted)] hover:text-[var(--ink)]"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full border border-[var(--hairline)] font-mono text-[10.5px] text-[var(--muted)] hover:text-[var(--crimson)] hover:border-[var(--crimson)] transition-colors disabled:opacity-40"
            data-testid="ce-refresh-btn"
          >
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>
      </div>

      {/* Source type legend strip */}
      <div className="mb-6 flex items-center gap-4 flex-wrap px-4 py-3 border border-[var(--hairline)] rounded-[6px] bg-[var(--surface-2)]">
        <div className="flex items-center gap-2">
          <Rss size={13} className="text-blue-600" />
          <span className="font-mono text-[10.5px] text-[var(--ink-2)]">
            <strong className="text-blue-700">RSS</strong> — All {data?.source_type_summary?.total_sources || 326} sources ingested via RSS feeds
          </span>
        </div>
        <span className="text-[var(--hairline-strong)]">|</span>
        <div className="flex items-center gap-2">
          <Shield size={13} className="text-amber-600" />
          <span className="font-mono text-[10.5px] text-[var(--ink-2)]">
            <strong className="text-amber-700">Official / Auth</strong> — {data?.source_type_summary?.official_auth || "—"} govt agencies & wire services
          </span>
        </div>
        <span className="text-[var(--hairline-strong)]">|</span>
        <div className="flex items-center gap-2">
          <Newspaper size={13} className="text-emerald-600" />
          <span className="font-mono text-[10.5px] text-[var(--ink-2)]">
            <strong className="text-emerald-700">Publications</strong> — {data?.source_type_summary?.publications || "—"} editorial outlets
          </span>
        </div>
      </div>

      {/* Top summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        <SummaryCard label="Countries" value={loading ? "…" : totals.countries || "—"}
          sub="Active coverage" icon={<Globe size={13} />} />
        <SummaryCard label="Stories" value={loading ? "…" : fmtK(totals.stories || 0)}
          sub={`${totals.rewritten || 0} rewritten`} icon={<Rss size={13} />} accent="text-blue-600" />
        <SummaryCard label="Brief Ready" value={loading ? "…" : fmtK(totals.with_brief || 0)}
          sub={`${pct(totals.with_brief, totals.stories)} of stories`}
          icon={<Newspaper size={13} />} accent="text-[var(--crimson)]" />
        <SummaryCard label="LLM Spend" value={loading ? "…" : fmt$(totals.total_llm_cost)}
          sub={`${fmt$(totals.rewrite_cost)} rewrite · ${fmt$(totals.brief_cost)} brief`}
          icon={<DollarSign size={13} />} accent="text-emerald-600" />
      </div>

      {/* Tab content */}
      {loading ? (
        <div className="flex items-center justify-center h-48 text-[var(--muted)] font-mono text-[12px]">
          Loading economics data…
        </div>
      ) : !data ? (
        <div className="flex items-center justify-center h-48 text-[var(--muted)] font-mono text-[12px]">
          No data available.
        </div>
      ) : (
        <>
          {activeTab === "coverage"   && <CoverageTab   rows={rows} totals={totals} />}
          {activeTab === "spend"      && <SpendTab       rows={rows} totals={totals} />}
          {activeTab === "conversion" && <ConversionTab  rows={rows} totals={totals} />}
        </>
      )}
    </section>
  );
}
